import { FC, KeyboardEvent, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Input } from '@/components';
import strings from '@/language/Swedish';
import { RegionType } from './svenskaRegioner';

const Container = styled.div`
  ${tw`flex flex-col gap-1`};
`;

const ChipContainer = styled.div`
  ${tw`flex flex-wrap gap-1`};
`;

const Chip = styled.span`
  ${tw`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-800 text-slate-50 text-[11px] leading-tight`};
`;

const ChipRemove = styled.button`
  ${tw`border-none bg-transparent text-slate-300 cursor-pointer p-0 text-[13px] leading-none hover:text-white`};
`;

const SmallInput = styled(Input)`
  ${tw`h-8 text-xs`};
`;

const ErrorHint = styled.span`
  ${tw`text-[11px] text-red-600`};
`;

interface Props {
  id: string;
  placeholder?: string;
  values: string[];
  onChange: (values: string[]) => void;
  // With suggestions set, input matches on names and chips store the code
  suggestions?: RegionType[];
}

const MultiValueInput: FC<Props> = ({
  id,
  placeholder,
  values,
  onChange,
  suggestions,
}) => {
  const [text, setText] = useState('');
  const [hasError, setHasError] = useState(false);

  const displayName = (value: string) =>
    suggestions?.find((item) => item.code === value)?.name ?? value;

  const addValue = () => {
    const raw = text.trim();
    if (!raw) return;

    let value = raw;
    if (suggestions) {
      const lower = raw.toLowerCase();
      const prefixMatches = suggestions.filter((item) =>
        item.name.toLowerCase().startsWith(lower)
      );
      const match =
        suggestions.find((item) => item.name.toLowerCase() === lower) ??
        suggestions.find((item) => item.code === raw) ??
        (prefixMatches.length === 1 ? prefixMatches[0] : undefined);

      if (!match) {
        setHasError(true);
        return;
      }
      value = match.code;
    }

    setHasError(false);
    if (!values.includes(value)) onChange([...values, value]);
    setText('');
  };

  const removeValue = (value: string) =>
    onChange(values.filter((item) => item !== value));

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addValue();
    } else if (event.key === 'Backspace' && !text && values.length) {
      removeValue(values[values.length - 1]);
    }
  };

  const listId = suggestions ? `${id}-options` : undefined;

  return (
    <Container>
      {values.length > 0 && (
        <ChipContainer>
          {values.map((value) => (
            <Chip key={value}>
              {displayName(value)}
              <ChipRemove
                type="button"
                aria-label={strings.sidebar.delete}
                onClick={() => removeValue(value)}
              >
                ×
              </ChipRemove>
            </Chip>
          ))}
        </ChipContainer>
      )}
      <SmallInput
        id={id}
        type="text"
        list={listId}
        placeholder={placeholder}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setHasError(false);
        }}
        onKeyDown={handleKeyDown}
        onBlur={addValue}
      />
      {suggestions && (
        <datalist id={listId}>
          {suggestions.map((item) => (
            <option key={item.code} value={item.name} />
          ))}
        </datalist>
      )}
      {hasError && <ErrorHint>{strings.registry.noRegionMatch}</ErrorHint>}
    </Container>
  );
};

export default MultiValueInput;
